import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, useMediaQuery, useTheme, Button, Grid } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { AptorApi, useAptorApi } from '../../../../../../../Api';
import { useApiValidationSnackbar } from '../../../../../../../Api/useValidationSnackbar';
import { Checklist, IListItem } from '../../../../../../AptorAdmin/Components/Checklist/Checklist.List/Checklist.List';
import { Panel } from '../../../../../../../Components';
import Loading from '../../../../../../../Components/Loading';
import { StepButtonBar } from './StepButtonBar';
import { PropertyError } from '../../../../../../../Api/AptorApi';
import { ChecklistForm } from '../../../../../../AptorAdmin/Components/Checklist/Checklist.Form';
import OtherRequirementCheckListInfo from './OtherRequirementCheckListInfo';

interface IRequirementChecklist {
  requirement: string;
  items: IListItem[];
}

interface Step2EditChecklistProps {
  reqId: number;
  unitId: number;
  onBack: () => void;
  onCancel: () => void;
  onComplete: () => void;
  updateChecklistInfo?: any;
  currentStepName?: string;
  setUpdateChecklistInfo?: () => void;
}

const Step2EditChecklist: React.FC<Step2EditChecklistProps> = ({
  reqId,
  unitId,
  onBack,
  onCancel,
  onComplete,
  updateChecklistInfo,
  currentStepName,
  setUpdateChecklistInfo,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { api, abortController } = useAptorApi();
  const { notifySuccess, notifyUnhandledException, notifyValidationErrors } = useApiValidationSnackbar();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState<string>();
  const [items, setItems] = useState<IListItem[]>([]);
  const [errors, setErrors] = useState<PropertyError[]>([]);
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(2);

  const fetcher = useCallback(
    () =>
      api.getRequirementChecklist<IRequirementChecklist>(reqId, unitId).then((x) => {
        if (abortController.current.signal.aborted) {
          return undefined;
        }
        return { name: x.requirement, items: x.items };
      }),
    [api, abortController, reqId, unitId],
  );

  const editor = (api: AptorApi, checklist: { items: IListItem[] }) =>
    api.editRequirementChecklist(reqId, unitId, checklist);

  useEffect(() => {
    fetcher().then((response) => {
      if (response) {
        setName(response.name);
        setItems(response.items);
        setLoading(false);
      }
    });
  }, [fetcher]);

  const addQuestion = (name: string) => setItems([...items, { name, type: 'question', groupItems: [] }]);
  const addGroup = (name: string) => setItems([...items, { name, type: 'group', groupItems: [] }]);
  const enableSave = useMemo(() => items.some((i) => i.type === 'question' || i.groupItems.length > 0), [items]);

  const handleSave = async () => {
    const action = async (api: AptorApi) => {
      if (!abortController.current.signal.aborted) {
        editor(api, { items }).then(() => {
          if (abortController.current.signal.aborted) {
            return;
          }
          notifySuccess();
          onComplete();
        });
      }
    };

    const onValidation = (errors: PropertyError[]) => {
      if (abortController.current.signal.aborted) {
        return;
      }
      notifyValidationErrors(errors) && setErrors(errors);
    };

    setIsSaving(true);
    try {
      await api.invoke(action, abortController.current, onValidation, notifyUnhandledException);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            minHeight: "350px",
            maxHeight: "400px",
            overflow: 'auto',
          }}
        >
          <Panel titleKey="admin.law-portal.law.checklist.heading" titleValues={{ name }} style={{ flex: 1, minHeight: 'auto' }}>
            <ChecklistForm addGroup={addGroup} addQuestion={addQuestion} errors={errors} />
          </Panel>
          <Panel titleKey="admin.law-portal.law.checklist.list.title" style={{ marginTop: '10px', flex: 1, minHeight: 'auto' }}>
            <Checklist items={items} updateItems={setItems} />
            {/* <Grid container justifyContent="flex-end">
              <Button disabled={!enableSave || isSaving} variant="outlined" onClick={handleSave}>
                {formatMessage({ id: 'form.save' })}
              </Button>
            </Grid> */}
          </Panel>
        </div>

        {updateChecklistInfo && currentStepName && (
          <div
            style={{
              width: isMobile ? '100%' : 'calc(50% - 5px)',
              maxHeight: "400px",
               minHeight: "400px",
              overflow: 'auto',
            }}
          >
            <OtherRequirementCheckListInfo
              updateConnectionInfo={updateChecklistInfo}
              currentStepName={currentStepName}
              onUpdate={setUpdateChecklistInfo}
            />
          </div>
        )}
      </div>

      <Box>
        <StepButtonBar
          onCancel={() => { }}
          onBack={() => { }}
          onNext={handleSave}
          nextLabel="form.save"
          nextDisabled={!enableSave || isSaving}
          isLoading={isSaving}
          formatMessage={formatMessage}
          showBack={false}
        />
      </Box>
    </div>
  );
};

export default Step2EditChecklist;
